/*
    Copyright © 2025 TRIPLES INNOVATION. All rights reserved.
    Proprietary and confidential. This source code is the exclusive property of
    TRIPLES INNOVATION and protected by copyright and trade secret law.
    Any unauthorized use, copying, modification, distribution, or disclosure
    is strictly prohibited without prior written permission from TRIPLES INNOVATION.
    Use is subject to a written license agreement with TRIPLES INNOVATION.
*/

import React, { useCallback, useMemo } from "react";
import {
    Typography,
    Box,
    useMediaQuery,
} from "@mui/material";
import { pieArcLabelClasses, PieChart } from '@mui/x-charts/PieChart';
import { useTheme } from '@mui/material/styles';
import Legend from "../../Legend";
import { useTranslation } from "react-i18next";
import { ClientTypes } from "../../../constants/reports/ClientTypes";
import { styled } from '@mui/material/styles';
import { LegendDirection, LegendShape } from "../../Legend/Legend";
import { formatPercentage } from "../../../utils/formatNumber";

interface PieChartDataItem {
    label: string;
    value: number;
}

interface ReportsPieChartProps {
    pieWidth: number;
    data: {
        uniqueClientsChartData: {
            data: PieChartDataItem[];
            title: string;
            totalValue: number;
        };
        totalVisitsChartData: {
            data: PieChartDataItem[];
            title: string;
            totalValue: number;
        };
    };
};

const PieChartContainer = styled('div')({
    alignItems: 'center',
    height: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
});

function ReportsPieChart({ pieWidth, data: { uniqueClientsChartData, totalVisitsChartData } }: ReportsPieChartProps) {
    const { t } = useTranslation("common");
    const theme = useTheme();
    const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('md'));

    const pieColors = {
        [ClientTypes.RARE]: theme.palette.primary.light,
        [ClientTypes.OCCASIONAL]: theme.palette.primary.main,
        [ClientTypes.LOYAL]: theme.palette.primary.dark,
        [ClientTypes.FAN]: theme.palette.secondary.main,
    };

    const calculateSlicesPercent = useCallback((data: PieChartDataItem[], totalValue: number) => {
        return data.map(d => {
            const rawPercent = totalValue > 0 ? (d.value / totalValue) * 100 : 0;
            return Math.round(rawPercent * 10) / 10; // one decimal
        });
    }, []);

    const legendItems = useMemo(() => {
        return [
            {
                color: theme.palette.primary.light,
                label: t("reports.clientTypes.rare-clients"),
            },
            {
                color: theme.palette.primary.main,
                label: t("reports.clientTypes.occasional-clients"),
            },
            {
                color: theme.palette.primary.dark,
                label: t("reports.clientTypes.loyal-clients"),
            },
            {
                color: theme.palette.secondary.main,
                label: t("reports.clientTypes.fan-clients"),
            },
        ];
    }, [theme.palette, t]);

    return (
        <Box
            display="flex"
            flexDirection="column"
            height="100%"
            justifyContent="space-between"
            width="100%"
        >
            <Box
                display="flex"
                flex={1}
                flexDirection={isTabletOrMobile ? "column" : "row"}
                alignContent={"center"}
                justifyContent="center"
                p={2}
            >
                {/* nr of unique clients chart */}
                <PieChartContainer>
                    <Typography
                        align="center"
                        gutterBottom
                        variant="h6"
                        width="100%"
                        sx={{
                            color: theme.palette.text.primary,
                        }}
                    >
                        {uniqueClientsChartData.title}
                    </Typography>
                    <Box width={pieWidth} height={pieWidth}>
                        <PieChart
                            margin={{ right: 0, bottom: 0 }}
                            width={pieWidth}
                            series={[
                                {
                                    data: uniqueClientsChartData.data.map((d, i) => {
                                        return {
                                            id: i,
                                            value: calculateSlicesPercent(uniqueClientsChartData.data, uniqueClientsChartData.totalValue)[i],
                                            label: d.label,
                                            color: pieColors[d.label as keyof typeof pieColors],
                                        };
                                    }),
                                    valueFormatter: ({ value }) => formatPercentage(value),
                                    arcLabel: item => formatPercentage(item.value),
                                    arcLabelMinAngle: 10,
                                },
                            ]}
                            slotProps={{
                                legend: {
                                    hidden: true,
                                }
                            }}
                            sx={{
                                [`& .${pieArcLabelClasses.root}`]: {
                                    fill: theme.palette.common.black,
                                    fontWeight: 800,
                                },
                            }}
                        />
                    </Box>
                </PieChartContainer>
                {isTabletOrMobile &&
                    <Box mb={3} mt={3} justifyContent="center" display="flex">
                        <Legend items={legendItems} direction={LegendDirection.COLUMN} shape={LegendShape.CIRCLE} />
                    </Box>
                }
                {/* nr of total visits chart */}
                <PieChartContainer>
                    <Typography
                        align="center"
                        variant="h6"
                        gutterBottom
                        sx={{ color: theme.palette.text.primary }}
                        width="100%"
                    >
                        {totalVisitsChartData.title}
                    </Typography>
                    <Box width={pieWidth} height={pieWidth}>
                        <PieChart
                            margin={{ right: 0, bottom: 0 }}
                            width={pieWidth}
                            series={[
                                {
                                    data: totalVisitsChartData.data.map((d, i) => {
                                        return {
                                            id: i,
                                            value: calculateSlicesPercent(totalVisitsChartData.data, totalVisitsChartData.totalValue)[i],
                                            label: d.label,
                                            color: pieColors[d.label as keyof typeof pieColors],
                                        };
                                    }),
                                    valueFormatter: ({ value }) => formatPercentage(value),
                                    arcLabel: item => formatPercentage(item.value),
                                    arcLabelMinAngle: 10,
                                },
                            ]}
                            slotProps={{
                                legend: {
                                    hidden: true,
                                }
                            }}
                            sx={{
                                [`& .${pieArcLabelClasses.root}`]: {
                                    fill: theme.palette.common.black,
                                    fontWeight: 800,
                                },
                            }}
                        />
                    </Box>
                </PieChartContainer>
            </Box>
            {!isTabletOrMobile &&
                <Box mb={3} justifyContent="center" display="flex">
                    <Legend items={legendItems} direction={LegendDirection.COLUMN} shape={LegendShape.CIRCLE} />
                </Box>
            }
        </Box>
    );
}

export default ReportsPieChart;